import type {
  DashboardItem,
  KpiChartView,
  KpiTableView,
  KpiWatchlistView,
  MoodCheckView,
  MySolvesView,
  MyVsmsView,
  MyTasksView,
  ProjectPulseView,
  KaizenTableView,
  SolveActionsView,
} from "@playbookai/ui";
import { slotResult } from "@playbookai/dashboard-client";
import type {
  KpiDef,
  TaskBucket,
  WidgetDataResolveResponse,
  WidgetDataResult,
} from "@playbookai/types";
import type {
  CoachingNudgeParams,
  DailyBriefParams,
  KpiSnapshot,
  StrategicHealthParams,
} from "@playbookai/widget-schemas";
import {
  CoachingNudgeOutputSchema,
  DailyBriefOutputSchema,
  KaizenListOutputSchema,
  KpiListOutputSchema,
  KpiSnapshotSchema,
  MoodTodayOutputSchema,
  MyActiveSolvesOutputSchema,
  MyTasksOutputSchema,
  MyVisibleVsmsOutputSchema,
  ProjectPulseOutputSchema,
  SOLVE_ACTION_GROUPINGS,
  SOLVE_ACTION_OPTIONAL_COLUMNS,
  SolveActionsOutputSchema,
  StrategicHealthOutputSchema,
  type SolveActionGrouping,
  type SolveActionOptionalColumn,
} from "@playbookai/widget-schemas";

/**
 * Resolve a dashboard item's slot results (batched widget-data endpoint) to
 * the widget body's view-model (`TView`) — or a bounded per-widget state
 * (§16.4): loading, legitimate empty, retryable error, invalid configuration.
 * One widget's failure never affects its siblings; the page stays usable.
 *
 * The resolved view is discriminated by kind and every ready payload is parsed
 * against its shared wire schema before the body receives it. Static types
 * alone cannot protect this runtime boundary from a stale or broken service.
 */

/** The per-kind view models the Personal Home widget bodies consume. */
export type HomeWidgetView =
  | { readonly kind: "singleKpi" | "chart"; readonly view: KpiChartView }
  | { readonly kind: "kpiWatchlist"; readonly view: KpiWatchlistView }
  | { readonly kind: "kpiTable"; readonly view: KpiTableView }
  | { readonly kind: "myTasks"; readonly view: MyTasksView }
  | { readonly kind: "projectPulse"; readonly view: ProjectPulseView }
  | { readonly kind: "strategicHealth"; readonly view: StrategicHealthParams }
  | { readonly kind: "dailyBrief"; readonly view: DailyBriefParams }
  | { readonly kind: "coachingNudge"; readonly view: CoachingNudgeParams }
  | { readonly kind: "moodCheck"; readonly view: MoodCheckView }
  | { readonly kind: "mySolves"; readonly view: MySolvesView }
  | { readonly kind: "myVsms"; readonly view: MyVsmsView }
  | { readonly kind: "kaizenTable"; readonly view: KaizenTableView }
  | { readonly kind: "solveActions"; readonly view: SolveActionsView };

export type HomeWidgetState =
  | ({ readonly state: "view" } & HomeWidgetView)
  | { readonly state: "loading" }
  | { readonly state: "empty"; readonly message: string }
  | { readonly state: "error"; readonly retryable: boolean }
  | { readonly state: "invalid"; readonly message: string };

/** Primary data slot per Personal Home kind (mirrors the pure contracts). */
const PRIMARY_SLOT: Partial<Record<DashboardItem["kind"], string>> = {
  singleKpi: "kpi",
  chart: "kpi",
  kpiWatchlist: "kpis",
  kpiTable: "kpis",
  myTasks: "tasks",
  projectPulse: "projects",
  strategicHealth: "summary",
  dailyBrief: "brief",
  coachingNudge: "nudge",
  moodCheck: "mood",
  mySolves: "solves",
  myVsms: "vsms",
  kaizenTable: "kaizens",
  solveActions: "actions",
};

/** Presentation params are host-resolved, so an unknown value falls back. */
function solveActionsGrouping(value: unknown): SolveActionGrouping {
  return SOLVE_ACTION_GROUPINGS.includes(value as SolveActionGrouping)
    ? (value as SolveActionGrouping)
    : "solve";
}

function solveActionsColumns(value: unknown): SolveActionOptionalColumn[] {
  return Array.isArray(value)
    ? (value as unknown[]).filter(
        (column): column is SolveActionOptionalColumn =>
          SOLVE_ACTION_OPTIONAL_COLUMNS.includes(
            column as SolveActionOptionalColumn,
          ),
      )
    : [];
}

export function toKpiDef(snapshot: KpiSnapshot): KpiDef {
  return {
    id: snapshot.id,
    name: snapshot.name,
    value: snapshot.value,
    numeric: snapshot.numeric,
    ...(snapshot.unit !== undefined ? { unit: snapshot.unit } : {}),
    status: snapshot.status,
    delta: snapshot.delta,
    deltaNumeric: snapshot.deltaNumeric,
    ...(snapshot.inverse !== undefined ? { inverse: snapshot.inverse } : {}),
    series: snapshot.series,
    threshold: snapshot.threshold ?? "",
    commentary: snapshot.commentary ?? "",
  };
}

function taskPriorities(
  params: Record<string, unknown>,
): readonly TaskBucket[] {
  return Array.isArray(params["priorities"])
    ? (params["priorities"] as unknown[]).filter(
        (b): b is TaskBucket => b === "today" || b === "later",
      )
    : ["today", "later"];
}

function kpiChartView(
  params: Record<string, unknown>,
  kpi: KpiDef,
): KpiChartView {
  return {
    kpi,
    ...(typeof params["chartType"] === "string"
      ? { chartType: params["chartType"] as KpiChartView["chartType"] }
      : {}),
    ...(typeof params["showDelta"] === "boolean"
      ? { showDelta: params["showDelta"] }
      : {}),
    ...(typeof params["showCommentary"] === "boolean"
      ? { showCommentary: params["showCommentary"] }
      : {}),
  };
}

export function resolveWidgetState(
  item: DashboardItem,
  response: WidgetDataResolveResponse | undefined,
  loading: boolean,
): HomeWidgetState {
  const params = (item.params ?? {}) as Record<string, unknown>;
  const slotKey = PRIMARY_SLOT[item.kind];
  if (slotKey === undefined) {
    return { state: "invalid", message: "Unsupported widget." };
  }
  const result: WidgetDataResult | undefined = slotResult(
    response,
    item.instanceId,
    slotKey,
  );
  if (result === undefined) {
    if (loading || response === undefined) return { state: "loading" };
    return {
      state: "invalid",
      message: "This widget isn't configured yet.",
    };
  }
  if (result.status === "error") {
    if (result.error.code === "INVALID_CONNECTION") {
      return {
        state: "invalid",
        message:
          item.kind === "singleKpi" || item.kind === "chart"
            ? "Pick a KPI to display."
            : "This widget isn't configured yet.",
      };
    }
    return { state: "error", retryable: result.error.retryable };
  }

  const empty = result.status === "empty";
  const data = result.status === "ready" ? result.data : null;
  const invalidReady = (): HomeWidgetState => ({
    state: "error",
    retryable: false,
  });
  switch (item.kind) {
    case "singleKpi":
    case "chart": {
      if (empty) {
        const hasSelection =
          typeof params["kpiId"] === "string" && params["kpiId"] !== "";
        return hasSelection
          ? { state: "empty", message: "This KPI has no data yet." }
          : { state: "invalid", message: "Pick a KPI to display." };
      }
      const parsed = KpiSnapshotSchema.safeParse(data);
      if (!parsed.success) return invalidReady();
      return {
        state: "view",
        kind: item.kind,
        view: kpiChartView(params, toKpiDef(parsed.data)),
      };
    }
    case "kpiWatchlist":
    case "kpiTable": {
      const parsed = empty ? null : KpiListOutputSchema.safeParse(data);
      if (parsed !== null && !parsed.success) return invalidReady();
      const kpis = parsed === null ? [] : parsed.data.kpis.map(toKpiDef);
      return { state: "view", kind: item.kind, view: { kpis } };
    }
    case "myTasks": {
      const parsed = empty ? null : MyTasksOutputSchema.safeParse(data);
      if (parsed !== null && !parsed.success) return invalidReady();
      return {
        state: "view",
        kind: item.kind,
        view: {
          tasks: parsed === null ? [] : parsed.data.tasks,
          mode: params["mode"] === "flat" ? "flat" : "ai",
          priorities: taskPriorities(params),
        },
      };
    }
    case "projectPulse": {
      const parsed = empty ? null : ProjectPulseOutputSchema.safeParse(data);
      if (parsed !== null && !parsed.success) return invalidReady();
      return {
        state: "view",
        kind: item.kind,
        view: { projects: parsed === null ? [] : parsed.data.projects },
      };
    }
    case "strategicHealth": {
      if (empty)
        return {
          state: "empty",
          message: "No strategic summary available yet.",
        };
      const parsed = StrategicHealthOutputSchema.safeParse(data);
      return parsed.success
        ? { state: "view", kind: item.kind, view: parsed.data }
        : invalidReady();
    }
    case "dailyBrief": {
      if (empty)
        return { state: "empty", message: "No brief available today." };
      const parsed = DailyBriefOutputSchema.safeParse(data);
      return parsed.success
        ? { state: "view", kind: item.kind, view: parsed.data }
        : invalidReady();
    }
    case "coachingNudge": {
      const parsed = empty ? null : CoachingNudgeOutputSchema.safeParse(data);
      if (parsed !== null && !parsed.success) return invalidReady();
      return {
        state: "view",
        kind: item.kind,
        view: { nudges: parsed === null ? [] : parsed.data.nudges },
      };
    }
    case "moodCheck": {
      const parsed = empty ? null : MoodTodayOutputSchema.safeParse(data);
      if (parsed !== null && !parsed.success) return invalidReady();
      const payload = parsed?.data;
      // The CHECK-IN is present only when the payload carries a score; the two
      // visibility facts are separate and resolve either way. An older resolver
      // that still answers `empty` lands here as "visible, no AI access", which
      // is the pre-dismissal behaviour rather than a broken widget.
      const view: MoodCheckView = {
        logged:
          payload?.score === undefined
            ? null
            : { score: payload.score, note: payload.note ?? null },
        hidden: payload?.hiddenToday ?? false,
        aiAccessEnabled: payload?.aiAccessEnabled ?? false,
      };
      return { state: "view", kind: item.kind, view };
    }
    case "mySolves": {
      const parsed = empty ? null : MyActiveSolvesOutputSchema.safeParse(data);
      if (parsed !== null && !parsed.success) return invalidReady();
      return {
        state: "view",
        kind: item.kind,
        view: { solves: parsed === null ? [] : parsed.data.solves },
      };
    }
    case "myVsms": {
      const parsed = empty ? null : MyVisibleVsmsOutputSchema.safeParse(data);
      if (parsed !== null && !parsed.success) return invalidReady();
      return {
        state: "view",
        kind: item.kind,
        view: { vsms: parsed === null ? [] : parsed.data.vsms },
      };
    }
    case "kaizenTable": {
      const parsed = empty ? null : KaizenListOutputSchema.safeParse(data);
      if (parsed !== null && !parsed.success) return invalidReady();
      return {
        state: "view",
        kind: item.kind,
        view: { rows: parsed === null ? [] : parsed.data.rows },
      };
    }
    case "solveActions": {
      // The provider always resolves `ready` — even for zero rows — because the
      // payload also carries the scope notes the widget must be able to state.
      // An `empty` here therefore means a resolver that predates this dataset,
      // and it renders as the widget's own empty view rather than as an error.
      const parsed = empty ? null : SolveActionsOutputSchema.safeParse(data);
      if (parsed !== null && !parsed.success) return invalidReady();
      const payload = parsed?.data ?? null;
      const rows = payload?.rows ?? [];
      // The single-SOLVE subtitle is derived from the DATA, not from the
      // filter: a "one SOLVE selected" filter that resolved to nothing has no
      // title to show, and a wide filter that happens to match one SOLVE does.
      const solveTitles = new Set(rows.map((row) => row.solveTitle));
      return {
        state: "view",
        kind: item.kind,
        view: {
          rows,
          groupBy: solveActionsGrouping(params["groupBy"]),
          columns: solveActionsColumns(params["columns"]),
          truncated: payload?.truncated ?? false,
          matchedCount: payload?.matchedCount ?? rows.length,
          unavailableSolveIds: payload?.unavailableSolveIds ?? [],
          dueFilterExcludesCountermeasures:
            payload?.dueFilterExcludesCountermeasures ?? false,
          scopeTruncated: payload?.scopeTruncated ?? false,
          singleSolveTitle:
            solveTitles.size === 1 ? [...solveTitles][0]! : null,
        },
      };
    }
    default:
      return { state: "invalid", message: "Unsupported widget." };
  }
}

interface MemoEntry {
  readonly params: DashboardItem["params"];
  readonly title: string | undefined;
  readonly result: WidgetDataResult | undefined;
  /** A missing slot means "loading" before the batch lands, "invalid" after. */
  readonly resolved: boolean;
  readonly loading: boolean;
  readonly state: HomeWidgetState;
}

/**
 * A resolver that returns the SAME state object while a widget's resolution
 * inputs are unchanged. `DashboardWidget` re-syncs its optimistic state
 * whenever the `data` prop changes identity, so a freshly built view object on
 * every render throws away the optimistic value — Mood Check reverted to "not
 * logged" between the check-in write and the refetch. One entry per instance;
 * the edit dialog's draft preview shares an instanceId with the grid card and
 * simply takes turns in the slot (correct, just uncached while it is open).
 */
export function createWidgetStateResolver(): (
  item: DashboardItem,
  response: WidgetDataResolveResponse | undefined,
  loading: boolean,
) => HomeWidgetState {
  const memo = new Map<string, MemoEntry>();
  return (item, response, loading) => {
    const slotKey = PRIMARY_SLOT[item.kind];
    const result =
      slotKey === undefined
        ? undefined
        : slotResult(response, item.instanceId, slotKey);
    const resolved = response !== undefined;
    const previous = memo.get(item.instanceId);
    if (
      previous !== undefined &&
      previous.params === item.params &&
      previous.title === item.title &&
      previous.result === result &&
      previous.resolved === resolved &&
      previous.loading === loading
    ) {
      return previous.state;
    }
    const state = resolveWidgetState(item, response, loading);
    memo.set(item.instanceId, {
      params: item.params,
      title: item.title,
      result,
      resolved,
      loading,
      state,
    });
    return state;
  };
}
